import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

function nameToString(name: unknown): string {
  if (name === null || name === undefined) return '';
  return String(name);
}

function descriptionToString(description: unknown): string {
  if (description === null || description === undefined) return '';
  return String(description);
}

// Strip name/description so the JSON blob shown in Monaco doesn't duplicate
// the values already rendered in the input fields above the editor.
export function stripMetaFields(config: object): object {
  if (config === null || typeof config !== 'object' || Array.isArray(config)) {
    return config;
  }
  const { name: _name, description: _description, ...rest } = config as Record<string, unknown>;
  return rest;
}

export interface UseCodeEditorMetaFieldsParams {
  open?: boolean;
  config: object;
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
}

export interface UseCodeEditorMetaFieldsResult {
  nameInput: string;
  descriptionInput: string;
  isJsonValid: boolean;
  metaFieldsHelperText: string | undefined;
  debouncedSetValue: ReturnType<typeof debounce<(newValue?: string) => void>>;
  handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDescriptionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  flushAndBuildSavePayload: () => string;
}

export function useCodeEditorMetaFields(
  params: UseCodeEditorMetaFieldsParams,
): UseCodeEditorMetaFieldsResult {
  const { open, config, value, setValue } = params;

  const valueRef = useRef(JSON.stringify(stripMetaFields(config), null, 4));
  const nameInputRef = useRef('');
  const descriptionInputRef = useRef('');
  const wasOpenRef = useRef(false);

  const [nameInput, setNameInput] = useState(() =>
    nameToString((config as Record<string, unknown>).name),
  );
  const [descriptionInput, setDescriptionInput] = useState(() =>
    descriptionToString((config as Record<string, unknown>).description),
  );

  // Seed editor state only when the drawer transitions closed -> open.
  // Re-seeding on every `config` identity change wiped in-progress edits
  // whenever the parent rerendered from background polling.
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = !!open;
    if (!open || wasOpen) return;

    const configRecord = config as Record<string, unknown>;
    const nameFromConfig = nameToString(configRecord.name);
    const descriptionFromConfig = descriptionToString(configRecord.description);
    nameInputRef.current = nameFromConfig;
    descriptionInputRef.current = descriptionFromConfig;
    setNameInput(nameFromConfig);
    setDescriptionInput(descriptionFromConfig);
    const serializedConfig = JSON.stringify(stripMetaFields(config), null, 4);
    valueRef.current = serializedConfig;
    setValue(serializedConfig);
  }, [config, open, setValue]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const debouncedSetValue = useMemo(
    () =>
      debounce((newValue?: string) => {
        const nextEditorValue = newValue ?? '';
        valueRef.current = nextEditorValue;
        setValue(nextEditorValue);
      }, 400),
    [setValue],
  );

  useEffect(() => {
    return () => {
      debouncedSetValue.cancel();
    };
  }, [debouncedSetValue]);

  const isJsonValid = useMemo(() => {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }, [value]);

  let metaFieldsHelperText: string | undefined;
  if (!isJsonValid) {
    metaFieldsHelperText = 'Fix JSON syntax to save changes.';
  }

  const flushAndBuildSavePayload = useCallback(() => {
    debouncedSetValue.flush();
    let serializedPayload = valueRef.current;
    try {
      const parsedForSave = JSON.parse(serializedPayload) as Record<string, unknown>;
      if (
        typeof parsedForSave !== 'object' ||
        parsedForSave === null ||
        Array.isArray(parsedForSave)
      ) {
        return serializedPayload;
      }
      const descriptionTrimmed = descriptionInputRef.current.trim();
      const descriptionForSave = descriptionTrimmed === '' ? null : descriptionInputRef.current;
      const savePayload = {
        ...parsedForSave,
        name: nameInputRef.current,
        description: descriptionForSave,
      };
      serializedPayload = JSON.stringify(savePayload, null, 4);
    } catch {
      // keep invalid JSON as-is; caller blocks save when isJsonValid is false
    }
    return serializedPayload;
  }, [debouncedSetValue]);

  const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextName = event.target.value;
    nameInputRef.current = nextName;
    setNameInput(nextName);
  }, []);

  const handleDescriptionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextDescription = event.target.value;
    descriptionInputRef.current = nextDescription;
    setDescriptionInput(nextDescription);
  }, []);

  return {
    nameInput,
    descriptionInput,
    isJsonValid,
    metaFieldsHelperText,
    debouncedSetValue,
    handleNameChange,
    handleDescriptionChange,
    flushAndBuildSavePayload,
  };
}
